import type {
  MonthlyPlan,
  CreateMonthlyPlanDto,
  UpdateMonthlyPlanDto,
  PaginatedResponse,
  PlanQueryParams,
  TrafficChannel,
  ChannelPaymentType,
} from '@ads-manager/shared';
import apiClient from './client';

// Traffic channel info
export interface TrafficChannelInfo {
  id: string;
  name: string;
  code: string;
  icon?: string;
  color?: string;
}

// Types for plans by branch view
export interface ContractorForecastData {
  leadsTarget: number | null;
  expectedCpl: number | null;
  budget: number | null;
}

export interface PlanData {
  id: string | null;
  leadsTarget: number | null;
  budget: number | null;
  expectedCpl: number | null;
  notifyThreshold: number | null;
  /** «Цена лида» — ставка за лид плана (снимок). Для предоплатных редактируется в плане. */
  perLeadRate: number | null;
}

export interface AdditionalCostsData {
  perLeadRate: number;
  fixedRate: number;
  vatRate: number;
}

export interface ContractorPlanData {
  contractorId: string;
  contractorName: string;
  contractorChannelId: string;
  paymentType: ChannelPaymentType;
  trafficChannel: TrafficChannelInfo;
  forecast: ContractorForecastData;
  plan: PlanData | null;
  additionalCosts: AdditionalCostsData;
}

// Plan approval status type
export type PlanApprovalStatus = 'DRAFT' | 'PENDING' | 'APPROVED' | 'REJECTED';

export interface BranchPlanGroup {
  branchId: string;
  branchName: string;
  contractors: ContractorPlanData[];
  // === ПОЛЯ СОГЛАСОВАНИЯ ===
  approvalStatus: PlanApprovalStatus;
  hasUnsavedChanges: boolean;
  approvedValues?: {
    totalLeads: number;
    totalBudget: number;
  };
  lastSentAt?: string;
}

export interface PlansByBranchResponse {
  year: number;
  month: number;
  trafficChannel?: TrafficChannelInfo;
  branches: BranchPlanGroup[];
}

export interface GetPlansByBranchParams {
  year: number;
  month: number;
  trafficChannelId?: string;
}

export interface BulkSavePlanItem {
  contractorChannelId: string;
  branchId: string;
  year: number;
  month: number;
  leadsTarget: number;
  budget: number;
}

export interface BulkSavePlansResponse {
  saved: number;
  created: number;
  updated: number;
}

// Upsert single plan types
export interface UpsertPlanRequest {
  contractorChannelId: string;
  branchId: string;
  year: number;
  month: number;
  leadsTarget: number;
  budget: number;
  notifyThreshold?: number | null;
  /** «Цена лида» для предоплатных каналов (бюджет = лиды × цена лида) */
  perLeadRate?: number | null;
}

export interface UpsertPlanResponse {
  id: string;
  contractorChannelId: string;
  branchId: string;
  year: number;
  month: number;
  leadsTarget: number;
  budget: number;
  notifyThreshold: number | null;
  perLeadRate: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContractorChannelOption {
  id: string;
  trafficChannelId: string;
  trafficChannel?: TrafficChannel;
  contractorId: string;
  contractorName: string;
}

export interface BranchOption {
  id: string;
  name: string;
}

// Available contractors types
export interface AvailableContractorData {
  contractorId: string;
  contractorName: string;
  contractorChannelId: string;
  trafficChannel: TrafficChannelInfo;
  additionalCosts: AdditionalCostsData;
}

export interface AvailableContractorsResponse {
  contractors: AvailableContractorData[];
  branchId: string;
  branchName: string;
  year: number;
  month: number;
}

export interface GetAvailableContractorsParams {
  year: number;
  month: number;
  branchId: string;
  trafficChannelId?: string;
}

export interface AddContractorsToBranchRequest {
  year: number;
  month: number;
  branchId: string;
  contractorChannelIds: string[];
}

export interface AddContractorsToBranchResponse {
  created: number;
  skipped: number;
}

// === ТИПЫ ДЛЯ СОГЛАСОВАНИЯ ПЛАНОВ ===

export interface SubmitForApprovalRequest {
  branchId: string;
  year: number;
  month: number;
}

export interface SubmitForApprovalResponse {
  success: boolean;
  plansSubmitted: number;
}

export interface RevertToApprovedRequest {
  branchId: string;
  year: number;
  month: number;
}

export interface RevertToApprovedResponse {
  success: boolean;
  plansReverted: number;
}

export interface ForceApprovePlanRequest {
  branchId: string;
  year: number;
  month: number;
}

export interface ForceApprovePlanResponse {
  success: boolean;
  plansApproved: number;
}

// === ТИПЫ ДЛЯ КАБИНЕТА ФИЛИАЛА ===

export interface ContractorPlanSummary {
  contractorId: string;
  contractorName: string;
  contractorChannelId: string;
  trafficChannel: TrafficChannelInfo;
  leadsTarget: number;
  budget: number;
  expectedCpl: number;
  perLeadRate: number;
  fixedRate: number;
  workCost: number; // leadsTarget * perLeadRate + fixedRate
  adBudget: number; // budget - workCost
}

export interface BranchPendingPlansResponse {
  year: number;
  month: number;
  branchId: string;
  branchName: string;
  status: PlanApprovalStatus;
  contractors: ContractorPlanSummary[];
  totals: {
    leads: number;
    budget: number;
    workCost: number;
    adBudget: number;
  };
  hasPendingApproval: boolean;
  lastSentAt?: string;
}

export interface PendingApprovalCountResponse {
  count: number;
}

export interface ApprovePlanRequest {
  year: number;
  month: number;
}

export interface ApprovePlanResponse {
  success: boolean;
  plansApproved: number;
}

export interface RejectPlanRequest {
  year: number;
  month: number;
}

export interface RejectPlanResponse {
  success: boolean;
  plansRejected: number;
}

// === ТИПЫ ДЛЯ ИСТОРИИ ПЛАНОВ ===

export interface GetBranchPlanHistoryParams {
  branchId: string;
  year: number;
  month: number;
  trafficChannelId?: string;
}

export interface DailyReportSummary {
  id: string;
  day: number;
  spent: number;
  leadsCount: number;
}

export interface PlanStats {
  totalSpent: number;
  totalLeads: number;
  remaining: number;
  actualCpl: number;
  completion: number;
  forecast: number;
}

export interface PlanWithStats extends Omit<MonthlyPlan, 'dailyReports'> {
  stats: PlanStats;
  dailyReports: DailyReportSummary[];
}

export const plansApi = {
  getAll: async (params?: PlanQueryParams): Promise<PaginatedResponse<PlanWithStats>> => {
    const response = await apiClient.get<PaginatedResponse<PlanWithStats>>('/plans', {
      params,
    });
    return response.data;
  },

  getById: async (id: string): Promise<PlanWithStats> => {
    const response = await apiClient.get<PlanWithStats>(`/plans/${id}`);
    return response.data;
  },

  create: async (data: CreateMonthlyPlanDto): Promise<MonthlyPlan> => {
    const response = await apiClient.post<MonthlyPlan>('/plans', data);
    return response.data;
  },

  update: async (id: string, data: UpdateMonthlyPlanDto): Promise<PlanWithStats> => {
    const response = await apiClient.patch<PlanWithStats>(`/plans/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<{ message: string }> => {
    const response = await apiClient.delete<{ message: string }>(`/plans/${id}`);
    return response.data;
  },

  getContractorChannelOptions: async (): Promise<ContractorChannelOption[]> => {
    const response = await apiClient.get<ContractorChannelOption[]>(
      '/plans/options/contractor-channels',
    );
    return response.data;
  },

  getBranchOptions: async (): Promise<BranchOption[]> => {
    const response = await apiClient.get<BranchOption[]>('/plans/options/branches');
    return response.data;
  },

  getPlansByBranch: async (params: GetPlansByBranchParams): Promise<PlansByBranchResponse> => {
    const response = await apiClient.get<PlansByBranchResponse>('/plans/by-branch', {
      params,
    });
    return response.data;
  },

  bulkSave: async (plans: BulkSavePlanItem[]): Promise<BulkSavePlansResponse> => {
    const response = await apiClient.post<BulkSavePlansResponse>('/plans/bulk', { plans });
    return response.data;
  },

  upsertPlan: async (data: UpsertPlanRequest): Promise<UpsertPlanResponse> => {
    const response = await apiClient.post<UpsertPlanResponse>('/plans/upsert', data);
    return response.data;
  },

  getAvailableContractors: async (
    params: GetAvailableContractorsParams,
  ): Promise<AvailableContractorsResponse> => {
    const response = await apiClient.get<AvailableContractorsResponse>(
      '/plans/available-contractors',
      { params },
    );
    return response.data;
  },

  addContractorsToBranch: async (
    data: AddContractorsToBranchRequest,
  ): Promise<AddContractorsToBranchResponse> => {
    const response = await apiClient.post<AddContractorsToBranchResponse>(
      '/plans/add-contractors-to-branch',
      data,
    );
    return response.data;
  },

  // === МЕТОДЫ СОГЛАСОВАНИЯ ПЛАНОВ ===

  submitForApproval: async (
    data: SubmitForApprovalRequest,
  ): Promise<SubmitForApprovalResponse> => {
    const response = await apiClient.post<SubmitForApprovalResponse>(
      '/plans/submit-approval',
      data,
    );
    return response.data;
  },

  revertToApproved: async (
    data: RevertToApprovedRequest,
  ): Promise<RevertToApprovedResponse> => {
    const response = await apiClient.post<RevertToApprovedResponse>(
      '/plans/revert-to-approved',
      data,
    );
    return response.data;
  },

  forceApprovePlan: async (
    data: ForceApprovePlanRequest,
  ): Promise<ForceApprovePlanResponse> => {
    const response = await apiClient.post<ForceApprovePlanResponse>(
      '/plans/force-approve',
      data,
    );
    return response.data;
  },

  // Обновить ставки/тип плана из текущего канала и отправить на пересогласование
  updatePlanRates: async (
    planId: string,
  ): Promise<{ success: boolean; planId: string; approvalStatus: string }> => {
    const response = await apiClient.post<{
      success: boolean;
      planId: string;
      approvalStatus: string;
    }>(`/plans/${planId}/update-rates`);
    return response.data;
  },

  // === МЕТОДЫ ДЛЯ КАБИНЕТА ФИЛИАЛА ===

  getBranchPendingPlans: async (
    year: number,
    month: number,
  ): Promise<BranchPendingPlansResponse> => {
    const response = await apiClient.get<BranchPendingPlansResponse>(
      '/plans/branch/pending',
      { params: { year, month } },
    );
    return response.data;
  },

  getPendingApprovalCount: async (): Promise<PendingApprovalCountResponse> => {
    const response = await apiClient.get<PendingApprovalCountResponse>(
      '/plans/branch/pending-count',
    );
    return response.data;
  },

  approvePlan: async (data: ApprovePlanRequest): Promise<ApprovePlanResponse> => {
    const response = await apiClient.post<ApprovePlanResponse>(
      '/plans/branch/approve',
      data,
    );
    return response.data;
  },

  rejectPlan: async (data: RejectPlanRequest): Promise<RejectPlanResponse> => {
    const response = await apiClient.post<RejectPlanResponse>(
      '/plans/branch/reject',
      data,
    );
    return response.data;
  },

  // === МЕТОДЫ ИСТОРИИ ПЛАНОВ ===

  getBranchPlanHistory: async (params: GetBranchPlanHistoryParams): Promise<BranchPlanGroup | null> => {
    const { branchId, ...queryParams } = params;
    const response = await apiClient.get<PlansByBranchResponse>('/plans/by-branch', {
      params: queryParams,
    });
    return response.data.branches.find((b: BranchPlanGroup) => b.branchId === branchId) ?? null;
  },
};

export default plansApi;
